import { Text } from "@/atoms";
import { BadgeTag } from "@/atoms/tag/BadgeTag";
import { cn } from "@/lib/utils";
import { Models } from "api/types";
import RewardIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import {
  ReferralRewardsCircleTracker,
  ReferralRewardsStatsCard,
} from "modules/referralRewards";

type ReferralRewardsAchievementsProps = {
  brandColor?: string;
  achievementProgress?: Models<"DisplayAchievementProgress">;
  redeemableAwardedReferralRewards?: Models<"DisplayRedeemableReferralReward">[];
  nextBonusRewardTitle?: string;
  referralReward?: Models<"DisplayAvailableReferralReward">;
};

export const ReferralRewardsAchievements = ({
  brandColor,
  achievementProgress,
  redeemableAwardedReferralRewards,
  nextBonusRewardTitle,
  referralReward,
}: ReferralRewardsAchievementsProps) => {
  const scrollbarStyles =
    "md:[scrollbar-width:thin] md:[scrollbar-color:rgba(0,0,0,0.2)_transparent] md:[&::-webkit-scrollbar]:block md:[&::-webkit-scrollbar]:h-2 md:[&::-webkit-scrollbar-track]:bg-transparent md:[&::-webkit-scrollbar-thumb]:bg-black/20 md:[&::-webkit-scrollbar-thumb]:rounded md:[&::-webkit-scrollbar-thumb:hover]:bg-black/30";

  const rgbBrandColor = brandColor
    ? brandColor
        .replace("#", "")
        .match(/.{2}/g)
        ?.map((hex) => parseInt(hex, 16))
        .join(", ")
    : "59, 130, 246";

  const completedReferrals =
    achievementProgress?.howManySuccessfulReferrals || 0;
  const targetReferrals = 3; // hardcoded, but this should be pulled from the backend at some point
  const completedCycles = Math.floor(completedReferrals / targetReferrals);
  const currentCycleProgress = completedReferrals % targetReferrals;
  const referralsNeeded = targetReferrals - currentCycleProgress;

  // Filter and extract bonus rewards
  const bonusRewards = redeemableAwardedReferralRewards
    ?.filter((reward) => reward.awardedReferralReward.isBonus)
    .map((reward) => ({
      title:
        reward.awardedReferralReward.referralRewardAtTheTime.rewardTitle.text ||
        "Bonus Reward",
      id: reward.awardedReferralReward.referralRewardId.value,
    }))
    .reverse(); // Reverse to get oldest first

  const renderProgressCard = (bonusRewardText?: string) => {
    return (
      <div
        className="relative flex flex-col gap-2 overflow-hidden rounded-2xl border border-gray-200 bg-white px-6 py-6 shadow-sm"
        style={{ width: "270px", minHeight: "160px" }}
      >
        <div
          className="pointer-events-none absolute"
          style={{
            width: "320px",
            height: "180px",
            right: "-160px",
            top: "10%",
            transform: "rotate(-30.445deg) translateY(-40%)",
            flexShrink: 0,
            opacity: 0.07,
            color: brandColor,
            maskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.01) 0%, rgba(${rgbBrandColor},.9) 100%)`,
            WebkitMaskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.01) 0%, rgba(${rgbBrandColor},.9) 100%)`,
          }}
        >
          <RewardIcon
            style={{
              width: "100%",
              height: "100%",
              color: brandColor,
              fill: "currentColor",
            }}
          />
        </div>

        <div className="mb-3 pl-2 pr-2 text-left">
          <Text variant="body1" className="font-bold">
            You are {referralsNeeded} referral{referralsNeeded !== 1 ? "s" : ""}{" "}
            away from {bonusRewardText}
          </Text>
        </div>

        <div className="px-2">
          <ReferralRewardsCircleTracker
            current={currentCycleProgress}
            max={targetReferrals}
          />
        </div>
      </div>
    );
  };

  const renderCompletedCard = (bonusRewardText: string) => {
    return (
      <div
        className="relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        style={{
          width: "270px",
          minHeight: "160px",
        }}
      >
        <div
          className="pointer-events-none absolute"
          style={{
            width: "280px",
            height: "160px",
            right: "-140px",
            top: "10%",
            transform: "rotate(-30.445deg) translateY(-40%)",
            flexShrink: 0,
            opacity: 0.15,
            color: brandColor,
            maskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.01) 0%, rgba(${rgbBrandColor},.9) 100%)`,
            WebkitMaskImage: `linear-gradient(to bottom, rgba(${rgbBrandColor},.01) 0%, rgba(${rgbBrandColor},.9) 100%)`,
          }}
        >
          <RewardIcon
            style={{
              width: "100%",
              height: "100%",
              color: brandColor,
              fill: "currentColor",
            }}
          />
        </div>

        <div className="mb-3">
          <BadgeTag
            color="secondary"
            className="inline-block rounded-[4px] bg-gray-100 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600"
          >
            Completed
          </BadgeTag>
        </div>

        <div className="mb-2">
          <Text
            variant="body1"
            className="font-bold"
            text={`You referred ${targetReferrals} friends and earned ${bonusRewardText}`}
          />
        </div>
      </div>
    );
  };

  const isRepeatCash = referralReward?.referralRewardType === "RepeatCash";
  const bonusRewardEnabled = referralReward?.bonusRewardEnabled ?? false;

  const totalEarnedInDollars =
    (achievementProgress?.howMuchCashInCents || 0) / 100;

  const renderCarousel = () => {
    const cards = [];

    for (let i = 0; i < completedCycles; i++) {
      const bonusReward = bonusRewards?.[i];
      const bonusText = bonusReward?.title || "a Bonus Reward";
      cards.push(
        <div key={`completed-${i}`}>{renderCompletedCard(bonusText)}</div>
      );
    }

    if (isRepeatCash && !bonusRewardEnabled) {
      cards.push(
        <div key="stats">
          <ReferralRewardsStatsCard
            totalReferred={completedReferrals}
            totalEarned={totalEarnedInDollars}
            brandColor={brandColor}
          />
        </div>
      );
    }

    const nextBonusReward = bonusRewards?.[completedCycles];
    const nextBonusText = nextBonusReward?.title || nextBonusRewardTitle;

    if (bonusRewardEnabled && (nextBonusRewardTitle || completedCycles === 0)) {
      cards.push(<div key="progress">{renderProgressCard(nextBonusText)}</div>);
    }

    if (cards.length === 0) {
      return null;
    }

    return (
      <div className="w-full">
        <div
          data-testid="achievements-carousel"
          className={cn(
            "flex flex-nowrap gap-3 overflow-y-visible overflow-x-scroll pb-4 pl-4 pr-4 sm:gap-4",
            scrollbarStyles,
            cards.length === 1 && "justify-center",
            cards.length > 1 && "snap-x snap-mandatory"
          )}
          style={cards.length > 1 ? { scrollPaddingLeft: "16px" } : undefined}
        >
          {cards.reverse().map((card) => (
            <div key={card.key} className="shrink-0 snap-start">
              {card}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="ReferralRewardsAchievements"
      className="bg-gray-50 pb-2 pt-6"
    >
      <div className="mb-4 px-4">
        <Text
          variant="h2"
          className="text-start text-sub2 uppercase text-black"
          text="Achievements"
        />
      </div>

      {renderCarousel()}
    </div>
  );
};
